import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AppShell } from "@/components/Nav";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Copy, Download, Trash2, ArrowLeft, Mail, Send } from "lucide-react";
import { CertMailDialog } from "@/components/CertMailDialog";
import { AutoMailDialog } from "@/components/AutoMailDialog";

const STATUSES = ["draft", "sent", "pending", "resolved", "rejected"];

export default function DisputeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [certMailOpen, setCertMailOpen] = useState(false);
  const [autoMailOpen, setAutoMailOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/disputes/${id}`);
      setD(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  const updateStatus = async (status) => {
    try {
      const { data } = await api.patch(`/disputes/${id}/status`, { status });
      setD(data);
      toast.success("Status updated");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const copyLetter = () => {
    if (!d?.letter) return;
    navigator.clipboard.writeText(d.letter);
    toast.success("Letter copied");
  };

  const downloadLetter = () => {
    if (!d?.letter) return;
    const blob = new Blob([d.letter], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `dispute-${d.bureau}-${d.creditor_snapshot || "letter"}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const remove = async () => {
    if (!confirm("Delete this dispute?")) return;
    await api.delete(`/disputes/${id}`);
    toast.success("Deleted");
    navigate("/app/disputes");
  };

  if (loading) return <AppShell><div className="text-[#A8ADA5]">Loading…</div></AppShell>;
  if (!d) return <AppShell><div className="text-[#A8ADA5]">Not found.</div></AppShell>;

  return (
    <AppShell>
      <button
        onClick={() => navigate("/app/disputes")}
        className="inline-flex items-center text-sm text-[#A8ADA5] hover:text-[#D5FF3F] mb-6 transition-colors font-mono uppercase tracking-widest"
        data-testid="dispute-back-btn"
      >
        <ArrowLeft size={14} className="mr-1.5" /> Back
      </button>

      <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="overline mb-3">Dispute letter</div>
          <h1 className="font-display text-4xl md:text-5xl">{d.creditor_snapshot}</h1>
          <p className="text-[#A8ADA5] mt-2 capitalize font-mono text-sm">
            {d.bureau} · {d.strategy?.replace("_", " ")}
          </p>
        </div>
        <div className="flex items-center gap-2" data-testid="dispute-actions">
          <Select value={d.status} onValueChange={updateStatus}>
            <SelectTrigger data-testid="dispute-status-select" className="w-36 rounded-sm bg-[#131518] border-[#26282C]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#131518] border-[#26282C] text-[#F5F3EE]">
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={copyLetter} data-testid="dispute-copy-btn" className="rounded-sm btn-outline">
            <Copy size={14} className="mr-2" /> Copy
          </Button>
          <Button onClick={downloadLetter} data-testid="dispute-download-btn" className="rounded-sm btn-outline">
            <Download size={14} className="mr-2" /> Download
          </Button>
          <Button
            onClick={() => setAutoMailOpen(true)}
            data-testid="dispute-auto-mail-btn"
            className="rounded-sm btn-lime"
          >
            <Send size={14} className="mr-2" /> Send via USPS
          </Button>
          <Button
            onClick={() => setCertMailOpen(true)}
            data-testid="dispute-certmail-btn"
            className="rounded-sm btn-outline"
          >
            <Mail size={14} className="mr-2" /> Print &amp; mail myself
          </Button>
          <Button variant="ghost" onClick={remove} data-testid="dispute-delete-btn" className="text-[#FF6B4A] hover:bg-[#FF6B4A]/10">
            <Trash2 size={14} />
          </Button>
        </div>
      </div>

      <CertMailDialog open={certMailOpen} onOpenChange={setCertMailOpen} disputeId={id} />
      <AutoMailDialog
        open={autoMailOpen}
        onOpenChange={setAutoMailOpen}
        disputeId={id}
        onSent={() => load()}
      />

      <div className="card-surface p-8 lg:p-12 card-elevate">
        <div className="overline mb-6">letter body · signed by you</div>
        <pre
          data-testid="dispute-letter-text"
          className="whitespace-pre-wrap font-mono text-sm leading-relaxed text-[#F5F3EE]"
        >
{d.letter}
        </pre>
      </div>

      <div className="mt-6 text-xs text-[#6D716C] max-w-2xl leading-relaxed">
        Review this letter carefully before sending. Print it, sign it, and mail it via USPS Certified
        Mail with Return Receipt Requested. Do not include original documents — keep the originals.
      </div>
    </AppShell>
  );
}
